import React, { useEffect, useCallback } from 'react'
import { useRouteMatch, useHistory } from 'react-router'
import { useSelector, useDispatch } from 'react-redux'
import { AppState } from '../../store'
import { ensureNumber } from '../../guards'
import { createUseStyles, useTheme } from 'react-jss'
import { Theme } from '@rsmus/react-ui'
import Icon from '../icon/icon'
import * as callToActionActions from './callToActionModule'
import * as confirmModalActions from '../../actions/confirmModalActions'
import { VERIFY_SUBMIT_READY_ID } from '../questionList/questionListButtons'

function styles({ palette }: Theme) {
  return {
    root: {
      backgroundColor: palette.informational.yellow,
      color: palette.common.white,
    },
    icon: {
      height: '1.5rem',
      width: '1.5rem',
      margin: '0.5rem'
    },
    messageText: {
      display: 'inline'
    },
    actionText: {
      color: palette.blue.dark,
      '&:hover': {
        cursor: 'pointer'
      }
    }
  }
}

type StyleReturn = keyof ReturnType<typeof styles>
const useStyles = createUseStyles<Theme, StyleReturn>(styles)

const CallToAction = () => {
  const route = useRouteMatch<{ engagementId: string }>()
  const history = useHistory();
  const theme = useTheme()
  const classes = useStyles(theme)
  const engagement = useSelector((state: AppState) => {
    return state.engagements[ensureNumber(route.params.engagementId)]
  })
  const busy = useSelector((appState: AppState) => appState.http.loading.includes('update-engagement-phase'))
  // True while any answer-save round-trip is in flight, OR while any
  // answer on this engagement has been locally modified but not yet
  // server-confirmed. Without these gates the yellow ribbon fires the
  // moment count equality is briefly reached (transient 54/54 after the
  // user finishes the last currently-visible question of section 1+2)
  // and lingers ~1 second after genuine completion before the SUBMIT
  // modal catches up. Gating on !isSaving && !hasDirtyAnswers aligns the
  // ribbon's appearance with the modal's auto-open in questionListButtons.
  const isSaving = useSelector((appState: AppState) => appState.http.saving.length > 0)
  const hasDirtyAnswers = useSelector((appState: AppState) => {
    const engagementId = ensureNumber(route.params.engagementId)
    const answers = appState.engagementQuestions[engagementId] || {}
    return Object.values(answers).some(a => !!a && a.isDirty)
  })
  // Set while questionListButtons is running its server-verify refetch
  // (external users, see that component's useEffect). Suppresses the
  // yellow ribbon during the refetch window so it doesn't flash in the
  // 990-EZ case where the refetch is about to reveal more visible
  // sections and drop completeness back below 100%.
  const isVerifyingSubmitReady = useSelector((appState: AppState) =>
    appState.http.loading.includes(VERIFY_SUBMIT_READY_ID)
  )
  const user = useSelector((appState: AppState) => appState.auth.user)
  const currentMessage = useSelector((appState: AppState) => {
    const messageQueue = appState.callToActionQueue
    if (messageQueue.length > 0 && messageQueue[messageQueue.length - 1]) {
      return messageQueue[messageQueue.length - 1]
    }
    return null
  })
  const dispatch = useDispatch()

  const completeMessage = useCallback(() => {
    const modalConfig = {
      phaseChange: 'PBC'
    }
    dispatch(confirmModalActions.setConfirmModalAction({ confirmModal: modalConfig }))
    const endOfPath = history.location.pathname.split('/').pop()
    const redirectPaths = ['comments', 'documents', 'flags', 'carryForward', 'reports']
    if (endOfPath && redirectPaths.includes(endOfPath)) {
      history.push(`/engagements/${route.params.engagementId}`)
    }

  }, [dispatch, history, route.params.engagementId])

  const addMessage = useCallback(() => {
    const message: callToActionActions.CallToActionMessage = {
      type: 'READY_FOR_REVIEW',
      messageText: 'This engagement is ready to ',
      actionText: 'submit for review',
      callback: () => completeMessage()
    }
    dispatch(callToActionActions.addCallToActionMessageAction({ message }))
  }, [dispatch, completeMessage])

  const popMessage = useCallback(() => {
    dispatch(callToActionActions.popCalltoActionMessageAction())
  }, [dispatch])

  useEffect(() => {
    if (user?.isExternal && engagement?.phase === 'pbc' && !busy) {
      // Match clientUserButtonConfig / calculatePercentComplete: prefer the
      // For-User / Visible fields that the rules engine keeps fresh, fall
      // back to the server-stored fields. Without this fallback the yellow
      // ribbon flashes -- it appears the moment a rules cycle equalizes the
      // stored fields, then disappears as soon as a list refetch overwrites
      // them with stale server values while the For-User / Visible fields
      // stay fresh.
      const successful =
        engagement.successfulForUserQuestions ?? engagement.successfulQuestions
      const total =
        engagement.totalVisibleQuestions ?? engagement.totalQuestions
      // Require total > 0 -- see engagementPhaseService.ts. 990-PF new
      // engagements land in PBC with totalQuestions=0 before the first
      // rules cycle populates counts; without this guard the yellow
      // ribbon flashes on before any work is done.
      const phaseComplete = total > 0 && successful >= total
      // Suppress the ribbon during edit-before-save (any dirty answer) and
      // during the save round-trip (isSaving). Only treat count equality
      // as real once both signals are clear, matching the SUBMIT modal's
      // gate so the ribbon and modal appear together.
      const stable = !isSaving && !hasDirtyAnswers && !isVerifyingSubmitReady
      const existingMessage = currentMessage?.type === 'READY_FOR_REVIEW'
      if (phaseComplete && stable && !existingMessage) {
        addMessage()
      }
      if ((!phaseComplete || !stable) && existingMessage) {
        popMessage()
      }
    } else if (user?.isExternal && engagement?.phase !== 'pbc') {
      popMessage()
    }
    // eslint-disable-next-line
  }, [user, engagement, busy, isSaving, hasDirtyAnswers, isVerifyingSubmitReady])

  if (currentMessage) {
    return (
      <div className={classes.root}>
        <Icon className={classes.icon} icon='infoCircle'></Icon>
        <p className={classes.messageText}>{currentMessage.messageText}<span className={classes.actionText} onClick={currentMessage.callback}>{currentMessage.actionText}</span>.</p>
      </div>
    )
  }
  return (
    null
  )
}

export default CallToAction